import { createBrowserRouter, Navigate } from 'react-router';
import { lazy, Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ProtectedRoute } from '@/features/auth/guards/ProtectedRoute';
import { SubscriptionGuard } from '@/features/auth/guards/SubscriptionGuard';
import { AuthCallback } from '@/features/auth/components/AuthCallback';
import { LogoutSuccess } from '@/features/auth/pages/LogoutSuccess';
import { AppLayout } from '@/features/app/layouts/AppLayout';
import { ApiErrorFallback } from '@/components/errors/ApiErrorBoundary';
import { LoadingState } from '@/components/common/LoadingState';

// Lazy load pages for code splitting
const Dashboard = lazy(() =>
  import('@/features/dashboard/pages/Dashboard').then((m) => ({ default: m.Dashboard }))
);
const Submissions = lazy(() =>
  import('@/features/submissions/pages/Submissions').then((m) => ({ default: m.Submissions }))
);
const SubmissionDetail = lazy(() =>
  import('@/features/submissions/pages/SubmissionDetail').then((m) => ({
    default: m.SubmissionDetail,
  }))
);
const Settings = lazy(() =>
  import('@/features/settings/pages/Settings').then((m) => ({ default: m.Settings }))
);
const Workspaces = lazy(() =>
  import('@/features/workspaces/pages/Workspaces').then((m) => ({ default: m.Workspaces }))
);
const WorkspaceSettings = lazy(() =>
  import('@/features/workspaces/pages/WorkspaceSettings').then((m) => ({
    default: m.WorkspaceSettings,
  }))
);
const Onboarding = lazy(() =>
  import('@/features/onboarding/pages/Onboarding').then((m) => ({ default: m.Onboarding }))
);
const Billing = lazy(() =>
  import('@/features/billing/pages/Billing').then((m) => ({ default: m.default }))
);
const Subscribe = lazy(() =>
  import('@/features/billing/pages/Subscribe').then((m) => ({ default: m.default }))
);
const NotFound = lazy(() =>
  import('@/features/error/pages/NotFound').then((m) => ({ default: m.NotFound }))
);

// Loading component using our new LoadingState
const PageLoader = () => <LoadingState type="page" message="Loading..." />;

// Simple login page that automatically redirects to WorkOS hosted UI
const LoginPage = () => {
  useEffect(() => {
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/callback');
    window.location.href = `${import.meta.env.VITE_API_URL}/auth/login?redirect_uri=${redirectUri}`;
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
        <p className="text-sm text-gray-600 dark:text-gray-400">Redirecting to sign in...</p>
      </div>
    </div>
  );
};

// Simple signup page that automatically redirects to WorkOS hosted UI with sign-up screen hint
const SignupPage = () => {
  useEffect(() => {
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/callback');
    window.location.href = `${import.meta.env.VITE_API_URL}/auth/login?redirect_uri=${redirectUri}&screen_hint=sign-up`;
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
        <p className="text-sm text-gray-600 dark:text-gray-400">Starting your free trial...</p>
      </div>
    </div>
  );
};

export const router = createBrowserRouter([
  // Auth routes
  {
    path: '/auth/login',
    element: <LoginPage />,
  },
  {
    path: '/login',
    element: <Navigate to="/auth/login" replace />,
  },
  {
    path: '/signup',
    element: <SignupPage />,
  },
  {
    path: '/logout-success',
    element: <LogoutSuccess />,
  },
  {
    path: '/auth/callback',
    element: <AuthCallback />,
    errorElement: (
      <ApiErrorFallback error={null} resetErrorBoundary={() => (window.location.href = '/')} />
    ),
  },
  {
    path: '/onboarding',
    element: (
      <ErrorBoundary FallbackComponent={ApiErrorFallback}>
        <Suspense fallback={<PageLoader />}>
          <Onboarding />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: (
      <ApiErrorFallback error={null} resetErrorBoundary={() => (window.location.href = '/')} />
    ),
    children: [
      // Subscribe page - accessible without subscription guard
      {
        path: 'subscribe',
        element: (
          <ErrorBoundary FallbackComponent={ApiErrorFallback}>
            <Suspense fallback={<PageLoader />}>
              <Subscribe />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      // All other routes protected by SubscriptionGuard
      {
        element: (
          <SubscriptionGuard>
            <AppLayout />
          </SubscriptionGuard>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard />
                </Suspense>
              </ErrorBoundary>
            ),
          },
          {
            path: 'submissions',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <Submissions />
                </Suspense>
              </ErrorBoundary>
            ),
          },
          {
            path: 'submissions/:id',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <SubmissionDetail />
                </Suspense>
              </ErrorBoundary>
            ),
          },
          {
            path: 'workspaces',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <Workspaces />
                </Suspense>
              </ErrorBoundary>
            ),
          },
          {
            path: 'workspaces/:id/settings',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <WorkspaceSettings />
                </Suspense>
              </ErrorBoundary>
            ),
          },
          {
            path: 'workspaces/:id/settings/:tab',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <WorkspaceSettings />
                </Suspense>
              </ErrorBoundary>
            ),
          },
          {
            path: 'settings',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              </ErrorBoundary>
            ),
          },
          {
            path: 'settings/:tab',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              </ErrorBoundary>
            ),
          },
          {
            path: 'billing',
            element: (
              <ErrorBoundary FallbackComponent={ApiErrorFallback}>
                <Suspense fallback={<PageLoader />}>
                  <Billing />
                </Suspense>
              </ErrorBoundary>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoader />}>
        <NotFound />
      </Suspense>
    ),
  },
]);
